import { AgentProcedure } from "../types";

/*******************************************************************************
 *
 * check-closing-keywords.sh — Detect PR bodies that strand issues.
 *
 * GitHub honours exactly ONE issue reference per closing keyword. A body
 * that writes `Closes #593, #594, #595` closes #593 and silently leaves
 * #594 and #595 open forever; `Closes #821 / #837` strands #837. The
 * only correct form is one keyword per issue:
 *
 *   Closes #593
 *   Closes #594
 *
 * The procedure is the single source of truth for closing-keyword
 * parsing across the pipeline. The `pr-workflow` rule calls it before
 * `gh pr create`; the `pr-reviewer` calls it to enumerate every linked
 * issue (pre-merge) and to verify each one actually closed (post-merge).
 *
 ******************************************************************************/

/**
 * Renders the `check-closing-keywords.sh` body.
 *
 * Split into its own module so the scanner's parsing contract — fence
 * skipping, blockquote skipping, inline-code stripping, and the
 * chained-reference walk that distinguishes a malformed keyword from
 * prose that merely mentions issue numbers — stays readable next to the
 * tests that lock it, rather than buried inside the github-workflow
 * bundle definition.
 */
export function renderCheckClosingKeywordsScript(): string {
  return [
    "#!/usr/bin/env bash",
    "# check-closing-keywords.sh — Detect PR bodies that strand issues.",
    "#",
    "# GitHub honours exactly ONE issue reference per closing keyword. A",
    "# body that writes `Closes #593, #594, #595` closes #593 and leaves",
    "# #594 and #595 open forever. The only correct form is one keyword",
    "# per issue, each on its own line:",
    "#",
    "#   Closes #593",
    "#   Closes #594",
    "#",
    "# Usage:",
    "#   .claude/procedures/check-closing-keywords.sh <pr-number>",
    "#   .claude/procedures/check-closing-keywords.sh --body-file <path>",
    "#",
    "# Output (one stable line per reference, then a summary line):",
    "#   CLOSES #<n> line=<L>",
    "#       A reference GitHub WILL honour (the first one after a keyword).",
    "#   STRANDED #<n> line=<L> keyword=<kw> first=#<n>",
    "#       A reference chained onto an earlier one. GitHub will NOT",
    "#       close it — the issue is stranded.",
    "#   CLOSING_KEYWORDS_OK closes=<K>",
    "#   CLOSING_KEYWORDS_MALFORMED closes=<K> stranded=<S>",
    "#   NO_CLOSING_KEYWORD",
    "#",
    "# Exit codes:",
    "#   0 — at least one closing keyword, every one well-formed",
    "#   1 — at least one stranded reference (malformed body)",
    "#   2 — argument error",
    "#   3 — required command not on PATH",
    "#   4 — could not read the PR body",
    "#   5 — no closing keyword found at all",
    "",
    "set -uo pipefail",
    "",
    "# ── helpers ──────────────────────────────────────────────────────────",
    "",
    "err() {",
    '  printf "check-closing-keywords.sh: %s\\n" "$*" >&2',
    "}",
    "",
    "usage() {",
    "  cat >&2 <<'USAGE'",
    "Usage: check-closing-keywords.sh <pr-number>",
    "       check-closing-keywords.sh --body-file <path>",
    "",
    "  <pr-number>          Positive integer PR number; the body is read",
    "                       via `gh pr view <n> --json body`.",
    "  --body-file <path>   Read the body from a file instead. Use this to",
    "                       check a drafted body BEFORE `gh pr create`.",
    "",
    "Emits one CLOSES / STRANDED line per issue reference, then a summary.",
    "Exits 1 when any reference is stranded by a multi-issue keyword.",
    "USAGE",
    "}",
    "",
    "# ── argument parsing ─────────────────────────────────────────────────",
    "",
    'mode=""',
    'arg=""',
    "",
    "while [[ $# -gt 0 ]]; do",
    '  case "$1" in',
    "    --body-file)",
    '      if [[ $# -lt 2 || -z "${2:-}" ]]; then',
    '        err "--body-file requires a value"',
    "        usage",
    "        exit 2",
    "      fi",
    '      if [[ -n "$mode" ]]; then',
    '        err "specify either a PR number or --body-file, not both"',
    "        exit 2",
    "      fi",
    '      mode="file"',
    '      arg="$2"',
    "      shift 2",
    "      ;;",
    "    -h|--help)",
    "      usage",
    "      exit 0",
    "      ;;",
    "    -*)",
    '      err "unknown option: $1"',
    "      usage",
    "      exit 2",
    "      ;;",
    "    *)",
    '      if [[ -n "$mode" ]]; then',
    '        err "unexpected extra argument: $1"',
    "        usage",
    "        exit 2",
    "      fi",
    '      mode="pr"',
    '      arg="$1"',
    "      shift",
    "      ;;",
    "  esac",
    "done",
    "",
    'if [[ -z "$mode" ]]; then',
    '  err "expected a PR number or --body-file <path>"',
    "  usage",
    "  exit 2",
    "fi",
    "",
    "# ── read the body ────────────────────────────────────────────────────",
    "",
    'body=""',
    "",
    'if [[ "$mode" == "file" ]]; then',
    '  if [[ ! -r "$arg" ]]; then',
    '    err "body file not readable: $arg"',
    "    exit 4",
    "  fi",
    '  body=$(cat "$arg")',
    "else",
    '  if ! [[ "$arg" =~ ^[1-9][0-9]*$ ]]; then',
    "    err \"PR number must be a positive integer (got: '$arg')\"",
    "    exit 2",
    "  fi",
    "  if ! command -v gh >/dev/null 2>&1; then",
    '    err "required command not found on PATH: gh"',
    "    exit 3",
    "  fi",
    "  if ! body=$(gh pr view \"$arg\" --json body -q '.body' 2>/dev/null); then",
    '    err "could not read the body of PR #${arg} via gh"',
    "    exit 4",
    "  fi",
    "fi",
    "",
    "# ── parsing contract ─────────────────────────────────────────────────",
    "#",
    "# Regexes live in single-quoted variables so backticks and other shell",
    "# metacharacters inside them are never expanded by [[ ]].",
    "#",
    "# kw_re matches a closing keyword plus its FIRST issue reference. The",
    "# leading (^|[^[:alnum:]_]) is a word boundary so `prefixes #1` is not",
    "# read as `fixes #1`.",
    "#",
    "# sep_re / ws_re walk the tail AFTER that first reference, matching",
    "# only references CHAINED directly onto it by a separator a human",
    "# would mistake for a list: `,`  `/`  `&`  `+`  `and`  `, and`, or",
    "# bare whitespace. Anything else — a period, a dash, a word, an",
    "# opening paren — terminates the chain. That is what keeps prose like",
    "# `Closes #893. See #607 and #860 for context.` from false-positiving:",
    "# the `.` after #893 ends the chain before `#607` is ever considered.",
    "",
    "fence_re='^[[:space:]]{0,3}(```+|~~~+)'",
    "quote_re='^[[:space:]]*>'",
    "kw_re='(^|[^[:alnum:]_])(closes|closed|close|fixes|fixed|fix|resolves|resolved|resolve)[[:space:]]*:?[[:space:]]*#([0-9]+)(.*)$'",
    "sep_re='^[[:space:]]*(,[[:space:]]*and|,|/|&|[+]|and)[[:space:]]*#([0-9]+)(.*)$'",
    "ws_re='^[[:space:]]+#([0-9]+)(.*)$'",
    "",
    "# Keywords are case-insensitive on GitHub, so the scan is too.",
    "shopt -s nocasematch",
    "",
    "# A single backtick, held in a variable so the code-span stripper",
    "# never has to quote one inline.",
    "bt='`'",
    "",
    "# Remove inline code spans (`...`) from a line. A PR body that",
    "# DISCUSSES the malformed form — writing `Closes #593, #594` inside",
    "# backticks — must not trip the detector.",
    "strip_code_spans() {",
    '  local s="$1"',
    '  local out=""',
    '  while [[ "$s" == *"$bt"*"$bt"* ]]; do',
    '    out="$out${s%%$bt*}"',
    '    s="${s#*$bt}"',
    '    s="${s#*$bt}"',
    "  done",
    '  printf \'%s%s\' "$out" "$s"',
    "}",
    "",
    "# ── scan ─────────────────────────────────────────────────────────────",
    "",
    "line_no=0",
    "in_fence=0",
    'fence_marker=""',
    "closes_count=0",
    "stranded_count=0",
    "",
    'while IFS= read -r raw || [[ -n "$raw" ]]; do',
    "  line_no=$((line_no + 1))",
    "  # gh returns CRLF bodies; strip the CR so end-of-line matching works.",
    "  raw=\"${raw//$'\\r'/}\"",
    "",
    "  # Fenced code blocks are quoted content, never the PR's own intent.",
    '  if [[ "$raw" =~ $fence_re ]]; then',
    '    marker="${BASH_REMATCH[1]:0:1}"',
    "    if [[ $in_fence -eq 0 ]]; then",
    "      in_fence=1",
    '      fence_marker="$marker"',
    '    elif [[ "$marker" == "$fence_marker" ]]; then',
    "      in_fence=0",
    '      fence_marker=""',
    "    fi",
    "    continue",
    "  fi",
    "  if [[ $in_fence -eq 1 ]]; then",
    "    continue",
    "  fi",
    "",
    "  # Blockquoted text is someone else's words being cited.",
    '  if [[ "$raw" =~ $quote_re ]]; then',
    "    continue",
    "  fi",
    "",
    '  line=$(strip_code_spans "$raw")',
    "",
    '  rest="$line"',
    '  while [[ "$rest" =~ $kw_re ]]; do',
    '    kw="${BASH_REMATCH[2]}"',
    '    first="${BASH_REMATCH[3]}"',
    '    tail="${BASH_REMATCH[4]}"',
    "",
    '    printf \'CLOSES #%s line=%s\\n\' "$first" "$line_no"',
    "    closes_count=$((closes_count + 1))",
    "",
    "    # Walk every reference chained onto this keyword's first one.",
    '    t="$tail"',
    "    while :; do",
    '      if [[ "$t" =~ $sep_re ]]; then',
    '        extra="${BASH_REMATCH[2]}"',
    '        t="${BASH_REMATCH[3]}"',
    '      elif [[ "$t" =~ $ws_re ]]; then',
    '        extra="${BASH_REMATCH[1]}"',
    '        t="${BASH_REMATCH[2]}"',
    "      else",
    "        break",
    "      fi",
    "      printf 'STRANDED #%s line=%s keyword=%s first=#%s\\n' \\",
    '        "$extra" "$line_no" "$kw" "$first"',
    "      stranded_count=$((stranded_count + 1))",
    "    done",
    "",
    '    rest="$t"',
    "  done",
    'done <<< "$body"',
    "",
    "# ── summary ──────────────────────────────────────────────────────────",
    "",
    "if [[ $closes_count -eq 0 ]]; then",
    '  echo "NO_CLOSING_KEYWORD"',
    "  exit 5",
    "fi",
    "",
    "if [[ $stranded_count -gt 0 ]]; then",
    "  printf 'CLOSING_KEYWORDS_MALFORMED closes=%s stranded=%s\\n' \\",
    '    "$closes_count" "$stranded_count"',
    '  err "GitHub honours only ONE issue reference per closing keyword."',
    '  err "Give every issue its own keyword, one per line:"',
    '  err "  Closes #123"',
    '  err "  Closes #456"',
    "  exit 1",
    "fi",
    "",
    "printf 'CLOSING_KEYWORDS_OK closes=%s\\n' \"$closes_count\"",
    "exit 0",
  ].join("\n");
}

/**
 * `check-closing-keywords.sh` — the deterministic guard against the
 * multi-issue-closing-keyword defect class.
 */
export const checkClosingKeywordsProcedure: AgentProcedure = {
  name: "check-closing-keywords.sh",
  description:
    "Parse a PR body (by PR number or --body-file) and report every closing-keyword issue reference as CLOSES (GitHub will honour it) or STRANDED (chained onto an earlier reference and silently ignored). Exits 1 when any reference is stranded. Skips fenced code blocks, blockquotes, and inline code spans.",
  content: renderCheckClosingKeywordsScript(),
};
